import { z } from 'zod';
import { CounterpartyCheck } from './cp-checks';

/* ─────────── Контрагенты: реестр, документы, представители, стоп-лист (раздел 6.12) ─────────── */

const DateStr = z.iso.date();
const Money = z.number().finite();

export const COUNTERPARTY_ROLES = {
  supplier: 'Поставщик',
  contractor: 'Подрядчик',
  manufacturer: 'Производитель',
  distributor: 'Дистрибьютор',
  designer: 'Проектировщик',
  customer: 'Заказчик',
  carrier: 'Перевозчик',
} as const;
export type CounterpartyRole = keyof typeof COUNTERPARTY_ROLES;
const RoleSchema = z.enum(Object.keys(COUNTERPARTY_ROLES) as [CounterpartyRole, ...CounterpartyRole[]]);

export const CP_DOC_KINDS = {
  vat: 'Свидетельство о постановке на учёт по НДС',
  power_of_attorney: 'Доверенность',
  license: 'Лицензия',
  certificate: 'Сертификат',
  insurance: 'Страхование',
  other: 'Прочее',
} as const;
export type CpDocKind = keyof typeof CP_DOC_KINDS;

export const CONTACT_ROLES = {
  signatory: 'Подписант',
  manager: 'Менеджер',
  accountant: 'Бухгалтер',
  other: 'Другое',
} as const;
export type ContactRole = keyof typeof CONTACT_ROLES;

/** За сколько дней до окончания документа портал напоминает (КА-04) */
export const DOC_WARN_DAYS = 30;

export const SupplierInput = z.object({
  name: z.string().trim().min(2).max(300),
  shortName: z.string().trim().max(120).nullable().optional(),
  bin: z
    .string()
    .trim()
    .regex(/^\d{12}$/, 'БИН или ИИН — 12 цифр')
    .nullable()
    .optional(),
  roles: z.array(RoleSchema).min(1, 'Укажите хотя бы одну роль'),
  categories: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
  regions: z.array(z.string().trim().min(1).max(80)).max(30).optional(),
  address: z.string().trim().max(500).nullable().optional(),
  phone: z.string().trim().max(60).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
  bank: z.string().trim().max(200).nullable().optional(),
  iik: z.string().trim().max(40).nullable().optional(),
  bik: z.string().trim().max(20).nullable().optional(),
  note: z.string().trim().max(2000).nullable().optional(),
});
export type SupplierInput = z.infer<typeof SupplierInput>;

export const SupplierSummary = z.object({
  id: z.uuid(),
  name: z.string(),
  shortName: z.string().nullable(),
  bin: z.string().nullable(),
  roles: z.array(RoleSchema),
  categories: z.array(z.string()),
  regions: z.array(z.string()),
  stopListed: z.boolean(),
  docsExpiring: z.number().int(),
  docsExpired: z.number().int(),
  contracts: z.number().int(),
  offers: z.number().int(),
});
export type SupplierSummary = z.infer<typeof SupplierSummary>;

export const CpDocInput = z.object({
  /** Есть у сохранённого документа: правка, а не новая запись — история документа не теряется */
  id: z.uuid().optional(),
  kind: z.enum(Object.keys(CP_DOC_KINDS) as [CpDocKind, ...CpDocKind[]]),
  number: z.string().trim().max(100).nullable().optional(),
  issuedOn: DateStr.nullable().optional(),
  validUntil: DateStr.nullable().optional(),
  note: z.string().trim().max(500).nullable().optional(),
});
export type CpDocInput = z.infer<typeof CpDocInput>;

export const ContactInput = z.object({
  id: z.uuid().optional(),
  fullName: z.string().trim().min(2).max(200),
  role: z.enum(Object.keys(CONTACT_ROLES) as [ContactRole, ...ContactRole[]]),
  phone: z.string().trim().max(60).nullable().optional(),
  email: z.string().trim().max(200).nullable().optional(),
});
export type ContactInput = z.infer<typeof ContactInput>;

export const StopInput = z.object({
  action: z.enum(['add', 'remove']),
  reason: z.string().trim().min(3).max(2000),
  basis: z.string().trim().max(500).nullable().optional(),
  until: DateStr.nullable().optional(),
});
export type StopInput = z.infer<typeof StopInput>;

export const SupplierCard = SupplierSummary.extend({
  address: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  bank: z.string().nullable(),
  iik: z.string().nullable(),
  bik: z.string().nullable(),
  note: z.string().nullable(),
  docs: z.array(
    CpDocInput.extend({
      id: z.uuid(),
      number: z.string().nullable(),
      issuedOn: DateStr.nullable(),
      validUntil: DateStr.nullable(),
      note: z.string().nullable(),
      state: z.enum(['valid', 'expiring', 'expired', 'open']),
    }),
  ),
  contacts: z.array(
    ContactInput.extend({ id: z.uuid(), phone: z.string().nullable(), email: z.string().nullable() }),
  ),
  stoplist: z.array(
    z.object({
      id: z.uuid(),
      action: z.enum(['add', 'remove']),
      reason: z.string(),
      basis: z.string().nullable(),
      until: DateStr.nullable(),
      byName: z.string().nullable(),
      at: z.string(),
    }),
  ),
  /** Проверки контрагента, новые сверху; checkStop — «стоп» по проверке без решения Директора (КА-10) */
  checks: z.array(CounterpartyCheck),
  checkStop: z.boolean(),
  /** Правила важности находок у этой компании (КА-10) */
  checkRules: z.record(z.string(), z.enum(['stop', 'attention', 'info'])),
  /** История: КП, договоры и оплаты (КА-03) */
  offerList: z.array(
    z.object({
      requestId: z.uuid(),
      requestNumber: z.string(),
      title: z.string(),
      projectCode: z.string(),
      isSelected: z.boolean(),
      receivedOn: DateStr.nullable(),
    }),
  ),
  contractList: z.array(
    z.object({
      id: z.uuid(),
      number: z.string(),
      subject: z.string(),
      status: z.string(),
      amount: Money.nullable(),
    }),
  ),
  paidTotal: Money.nullable(),
});
export type SupplierCard = z.infer<typeof SupplierCard>;
